import type { ReactNode } from "react";
import { Platform, Pressable, View } from "react-native";

import { cn } from "@/lib/ui/cn";
import { slopToFloor, touchFloor } from "@/lib/ui/touchTarget";
import { spaceActivates } from "@/lib/ui/webKeyboard";

/** What the disc paints (`h-9 w-9`), as a number the slop arithmetic can use. */
const DISC = 36;

/**
 * One icon action in a navigation app bar.
 *
 * Every app-bar action shares this geometry. Boxes of different widths in one `gap-1` row leave
 * uneven gaps between the glyphs, and the icons look off-center.
 *
 * On native the touch target is `hitSlop`, not a bigger box. The slop reaches the platform minimum
 * (44pt iOS, 48dp Android) without enlarging the laid-out frame. That matters on iOS 26: the system
 * draws a glass capsule behind `headerRight`, sized and positioned from the frame, and a frame
 * padded out to 44 puts the glyph below the capsule's center.
 *
 * `h-9` is a real 36pt only because `tailwind.config.js` defines the box-dimension scale in points;
 * NativeWind otherwise resolves rem at `inlineRem: 14`. The sums are literal: 36 + 4 + 4 = 44 on
 * iOS, 36 + 6 + 6 = 48 on Android. The labelled app-bar controls that share this baseline,
 * `activity/ShowVoidedToggle` and `labels/SelectAllToggle` on Android and web, are `h-9` for the
 * same reason, so labelled and icon actions sit on one line.
 *
 * On web the frame is the target, because `react-native-web`'s `Pressable` ignores `hitSlop`.
 * Mobile web is a touch surface with no capsule to align to, so the frame takes a 44 minimum and
 * the 36 disc sits inside it.
 */
export function AppBarAction(
  {
    onPress,
    accessibilityLabel,
    accessibilityRole = "button",
    accessibilityState,
    children,
    active,
    disabled,
  }: {
    onPress: () => void;
    accessibilityLabel: string;
    /** Use `switch` for a toggle; it also exposes `checked` and activates on Space on web. */
    accessibilityRole?: "button" | "link" | "switch";
    accessibilityState?: { checked?: boolean; selected?: boolean; disabled?: boolean; };
    children: ReactNode;
    /** Paints the disc in the Signal, for a toggle that is on. */
    active?: boolean;
    disabled?: boolean;
  },
) {
  // Symmetric on every platform. RN clips hitSlop to the parent's bounds, so this relies on every
  // `NativeAppBarActions` row centering its content; a row that pins content to its top leaves no
  // room above the disc and loses the top half of the slop.
  //
  // 36 painted + this = the platform floor. `slopToFloor` is the single place that arithmetic
  // lives, so no call site copies half of it. The sum holds only because `h-9` is a real 36pt.
  const slop = slopToFloor(DISC);
  return (
    <Pressable
      onPress={onPress}
      disabled={disabled}
      hitSlop={slop}
      accessibilityRole={accessibilityRole}
      accessibilityLabel={accessibilityLabel}
      accessibilityState={accessibilityState}
      aria-checked={accessibilityRole === "switch" ? accessibilityState?.checked : undefined}
      {...(accessibilityRole === "switch" && !disabled ? spaceActivates(onPress) : {})}
      style={appBarTouchFrame()}
      className={cn(
        "items-center justify-center active:opacity-70",
        disabled && "opacity-40",
      )}
    >
      <View className="h-9 w-9 items-center justify-center rounded-full">
        {
          /* The on-state is its own layer, mounted only while on. Android paints a view square,
            ignoring `rounded-full`, once its background color changes after mount; a view that
            mounts with the color keeps its radius. */
        }
        {active ? <View className="absolute inset-0 rounded-full bg-primary" /> : null}
        {children}
      </View>
    </Pressable>
  );
}

/**
 * Returns the frame an app-bar control lays out at on the current platform.
 *
 * Nothing on native, where `hitSlop` carries the target and a bigger box would push the glyph off
 * the iOS 26 capsule's axis. A 44 minimum on web, where `hitSlop` does not exist.
 *
 * Exported for a text action such as Inventory's "Cancel", which must be as wide as its word, so
 * it cannot be a 36pt disc but still needs a target the same size as its neighbors in the row.
 */
export function appBarTouchFrame() {
  // Built per call rather than hoisted: `touchFloor()` reads the platform when asked, so a
  // test that flips `Platform.OS` gets the right number instead of one frozen at import.
  return Platform.OS === "web"
    ? { minHeight: touchFloor(), minWidth: touchFloor() }
    : undefined;
}
